import { auth } from '@/lib/firebase';

type RetryableCall<T> = () => Promise<T>;

const AUTH_ERROR_CODES = new Set([
  'unauthenticated',
  'functions/unauthenticated',
  'auth/id-token-expired',
  'auth/user-token-expired',
  'auth/user-disabled',
]);

type ErrorLike = {
  code?: string;
  message?: string;
  status?: number;
};

const normalize = (value: unknown): string => (typeof value === 'string' ? value.trim().toLowerCase() : '');

export function isAuthExpiredError(error: unknown): boolean {
  const maybeError = (error ?? {}) as ErrorLike;
  const code = normalize(maybeError.code);
  const message = normalize(maybeError.message);
  const status = typeof maybeError.status === 'number' ? maybeError.status : undefined;

  if (code && AUTH_ERROR_CODES.has(code)) {
    return true;
  }
  if (status === 401) {
    return true;
  }
  return (
    message.includes('unauthenticated') ||
    message.includes('token expired') ||
    message.includes('id token expired') ||
    message.includes('status code 401') ||
    message.includes('http 401') ||
    message.includes('unauthorized')
  );
}

export async function ensureFreshToken(forceRefresh = true): Promise<string> {
  try {
    await (auth as any)?.authStateReady?.();
  } catch {
    // Older SDK versions may not support authStateReady.
  }

  const user = auth.currentUser;
  if (!user) {
    throw new Error('Please sign in again to continue.');
  }
  return user.getIdToken(forceRefresh);
}

export async function withAuthRetry<T>(run: RetryableCall<T>): Promise<T> {
  try {
    return await run();
  } catch (error) {
    if (!isAuthExpiredError(error)) {
      throw error;
    }
    await ensureFreshToken(true);
    return run();
  }
}
